import axios from 'axios';
import type { ApiResponse, Product, Post, Menu, Settings } from '@/types/api';

// 获取API基础URL
// 在服务端渲染时，使用127.0.0.1而不是localhost，避免连接问题
const getApiBaseUrl = () => {
  const envUrl = process.env.NEXT_PUBLIC_API_URL;
  if (envUrl) {
    return envUrl;
  }
  
  // 服务端渲染时使用127.0.0.1，客户端使用localhost
  if (typeof window === 'undefined') {
    return 'http://127.0.0.1:3000/api';
  }
  return 'http://localhost:3000/api';
};

const API_BASE_URL = getApiBaseUrl();

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器
apiClient.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
apiClient.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error) => {
    // 只在客户端或开发环境输出详细错误日志
    const isClient = typeof window !== 'undefined';
    const isDev = process.env.NODE_ENV === 'development';
    
    // 保留完整的错误信息，包括 response 对象
    if (error.response) {
      // 服务器返回了错误响应
      // 在开发环境输出所有错误，包括404
      if (isClient || isDev) {
        console.error('API Error Response:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
          url: error.config?.url,
          fullUrl: error.config?.baseURL + error.config?.url,
        });
      }
    } else if (error.request) {
      // 请求已发出但没有收到响应
      if (isClient || isDev) {
        console.error('API Error Request:', {
          message: error.message,
          url: error.config?.url,
        });
      }
    } else {
      // 其他错误
      if (isClient || isDev) {
        console.error('API Error:', error.message || error);
      }
    }
    return Promise.reject(error);
  }
);

// 产品API
export const productAPI = {
  getList: (params?: {
    page?: number;
    limit?: number;
    status?: string;
    categoryId?: string;
    categorySlug?: string;
    search?: string;
    featured?: boolean;
  }): Promise<ApiResponse<Product[]>> => 
    apiClient.get('/products', { params }),
  
  getById: (id: string): Promise<ApiResponse<Product>> => 
    apiClient.get(`/products/${id}`),
  
  getCategories: (): Promise<ApiResponse<ProductCategory[]>> => 
    apiClient.get('/products/categories/list'),
};

// 文章API
export const postAPI = {
  getList: (params?: {
    page?: number;
    limit?: number;
    status?: string;
    categoryId?: string;
    search?: string;
  }): Promise<ApiResponse<Post[]>> => 
    apiClient.get('/posts', { params }),
  
  getById: (id: string): Promise<ApiResponse<Post>> => 
    apiClient.get(`/posts/${id}`),
  
  getBySlug: (slug: string): Promise<ApiResponse<Post>> => 
    apiClient.get(`/posts/slug/${slug}`),
  
  getCategories: (): Promise<ApiResponse<Category[]>> => 
    apiClient.get('/posts/categories/list'),
  
  getTags: (): Promise<ApiResponse<any[]>> => 
    apiClient.get('/posts/tags/list'),
};

// 菜单API
export const menuAPI = {
  getList: (location?: string): Promise<ApiResponse<Menu[]>> => 
    apiClient.get('/menus', { params: { location } }),
};

// Banner/轮播图API
export interface Banner {
  id: string;
  title?: string;
  subtitle?: string;
  imageUrl: string;
  link?: string;
  sortOrder: number;
  status: 'active' | 'inactive';
}

export const bannerAPI = {
  getList: (status?: string): Promise<ApiResponse<Banner[]>> => 
    apiClient.get('/banners', { params: status ? { status } : {} }),
};

// 设置API
export const settingAPI = {
  getAll: async (group?: string): Promise<Settings> => {
    try {
      // apiClient拦截器已经返回了 response.data
      // 后端返回格式：{ data: settingsObj }
      // 拦截器处理后：{ data: settingsObj }（response.data）
      const response = await apiClient.get<{ data: Settings }>('/settings', { params: { group } });
      
      // 由于拦截器返回 response.data，所以 response 可能是 { data: Settings } 或 Settings
      // 检查是否有嵌套的data
      if (response && typeof response === 'object' && 'data' in response && response.data) {
        return response.data as Settings;
      }
      
      // 如果没有嵌套的data，直接返回response（可能是Settings对象）
      return (response as any) as Settings;
    } catch (error) {
      console.error('[settingAPI.getAll] Error:', error);
      // 返回空对象，避免阻塞渲染
      return {} as Settings;
    }
  },
};

// 表单API
export const formAPI = {
  submit: (slug: string, data: Record<string, any>): Promise<any> =>
    apiClient.post(`/forms/${slug}/submit`, { data }),
};

// 案例API
export interface Case {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt?: string;
  featuredImage?: string;
  status: 'published' | 'draft';
  viewCount?: number;
  publishedAt?: string;
  createdAt: string;
  updatedAt: string;
  category?: {
    id: string;
    name: string;
    slug: string;
  };
  author?: {
    id: string;
    username: string;
  };
}

export const caseAPI = {
  getList: (params?: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<ApiResponse<Case[]>> => 
    apiClient.get('/cases', { params }),
  
  getById: (id: string): Promise<ApiResponse<Case>> => 
    apiClient.get(`/cases/${id}`),
  
  getBySlug: async (slug: string): Promise<ApiResponse<Case>> => {
    // 通过slug获取案例，先获取列表然后查找
    const response = await apiClient.get<ApiResponse<Case[]>>('/cases', { 
      params: { limit: 100, status: 'published' } 
    });
    const cases = response.data || [];
    const caseItem = cases.find((c) => c.slug === slug);
    
    if (!caseItem) {
      throw new Error('Case not found');
    }
    
    return {
      success: true,
      data: caseItem,
    } as ApiResponse<Case>;
  },
};

// 页面API
export interface PageSection {
  id: string;
  type: string;
  title: string;
  content: string;
  order: number;
  visible: boolean;
  settings?: {
    elementorCol?: string;
    [key: string]: any;
  };
}

export interface Page {
  id: string;
  title: string;
  slug: string;
  content?: string;
  excerpt?: string;
  sections?: PageSection[];
  template: 'default' | 'product-list' | 'news-list' | 'media';
  status: 'published' | 'draft';
  headerTitle?: string;
  headerSubtitle?: string;
  headerBackgroundImage?: string;
  headerBackgroundColor?: string;
  showBreadcrumb: boolean;
  seoTitle?: string;
  seoDescription?: string;
  seoKeywords?: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export const pageAPI = {
  getBySlug: (slug: string): Promise<ApiResponse<Page>> =>
    apiClient.get(`/pages/slug/${slug}`),
};

// 视频API
export interface Video {
  id: string;
  title: string;
  description?: string;
  thumbnail?: string;
  videoUrl: string;
  category?: string;
  duration?: string;
  viewCount: number;
  status: 'published' | 'draft';
  createdAt: string;
  updatedAt: string;
}

export interface VideoListResponse {
  data: Video[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

// 联系消息API
export interface ContactMessage {
  id: string;
  name: string;
  email: string;
  message: string;
  ipAddress?: string;
  userAgent?: string;
  status: 'new' | 'read' | 'replied';
  createdAt: string;
  updatedAt: string;
}

export interface CreateContactMessageDto {
  name: string;
  email: string;
  message: string;
}

export const contactAPI = {
  create: (data: CreateContactMessageDto): Promise<ApiResponse<ContactMessage>> =>
    apiClient.post('/contact', data),
};

// 搜索API
export interface SearchResult {
  id: string;
  type: 'product' | 'post' | 'case' | 'download' | 'video';
  title: string;
  slug?: string;
  url: string;
  description?: string;
  excerpt?: string;
  featuredImage?: string;
  thumbnail?: string;
  category?: {
    name: string;
    slug: string;
  };
}

export interface SearchResponse {
  data: SearchResult[];
  total: number;
  query: string;
  type: string;
}

export interface SearchParams {
  q: string;
  type?: 'all' | 'products' | 'posts' | 'cases' | 'downloads' | 'videos';
  limit?: number;
}

export const searchAPI = {
  search: (params: SearchParams): Promise<SearchResponse> =>
    apiClient.get('/search', { params }),
};

export const videoAPI = {
  getList: (params?: {
    page?: number;
    limit?: number;
    status?: string;
    category?: string;
    search?: string;
  }): Promise<VideoListResponse> => 
    apiClient.get('/videos', { params }),
  
  getById: (id: string): Promise<{ data: Video }> => 
    apiClient.get(`/videos/${id}`),
};

// 导出类型供其他地方使用
import type { ProductCategory, Category } from '@/types/api';
export type { ProductCategory, Category };

